import { useMutation, useQueryClient } from "@tanstack/react-query";
import { ClientInferResponseBody } from "@ts-rest/core";

import { contract } from "#app/http/api";
import { logger } from "#app/monitoring/Logger";
import {
  getQuoteRequestLineQueryData,
  quoteRequestLineQuery,
  setQuoteRequestLineQueryData,
} from "#modules/quoteRequestLines/infra/quoteRequestLine.query";
import { useAssembliesContext } from "#modules/quoteRequests/context/AssembliesContext";
import { useAnalytics, AnalyticsEvent } from "#shared/useAnalytics";
import { JobStatusSchema } from "shared";

import { predictQuoteRequestLine } from "../../quoteRequestLines/infra/predictQuoteRequestLine.api";

type PredictProcessStepsResponseBody = ClientInferResponseBody<
  typeof contract.quoteRequestLines.predict
>;

type Options = {
  onSuccess?: (prediction: PredictProcessStepsResponseBody | null) => void;
};

export const usePredictProcessSteps = (options?: Options) => {
  const queryClient = useQueryClient();
  const { assemblies } = useAssembliesContext();
  const analytics = useAnalytics();

  const { mutateAsync, ...rest } = useMutation({
    mutationFn: predictQuoteRequestLine,
    onMutate: ({ quoteRequestLineId }) => {
      analytics.capture(AnalyticsEvent.PredictionStarted, {
        quote_request_line_id: quoteRequestLineId,
      });
      const currentAssembly = assemblies.find(
        (assembly) => assembly.quoteRequestLine.id === quoteRequestLineId,
      );

      const childrenQuoteRequestLineIds = currentAssembly?.children.map(
        (child) => child.quoteRequestLine.id,
      );

      if (!childrenQuoteRequestLineIds) {
        return;
      }

      childrenQuoteRequestLineIds.forEach((id) => {
        const currentQuoteRequestLine = getQuoteRequestLineQueryData(queryClient, id);

        if (!currentQuoteRequestLine) {
          return;
        }

        setQuoteRequestLineQueryData(queryClient, id, {
          ...currentQuoteRequestLine,
          predictionStatus: JobStatusSchema.enum.STANDBY,
        });
      });
    },
    onSuccess: async (data) => {
      if (data) {
        void data.quoteRequestLinesIds.map(async (id) => {
          await queryClient.invalidateQueries(quoteRequestLineQuery({ id }));
        });

        analytics.capture(AnalyticsEvent.PredictionCompleted, {
          count: data.quoteRequestLinesIds.length,
          quote_request_line_ids: data.quoteRequestLinesIds,
        });

        options?.onSuccess?.(data);
      }
    },
    onError: (error) => {
      logger.error(error);
    },
  });

  return { predictProcessSteps: mutateAsync, ...rest };
};
